import type React from "react";

import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconButton, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { Invoice } from "api/services/admin/schemas";
import { BaseButton } from "shared/atoms/BaseButton/BaseButton";
import { EmptyResultOverlay } from "shared/molecules/EmptyResultOverlay/EmptyResultOverlay";
import { MUIDataGrid } from "shared/molecules/MUIDataGrid";
import { convertCentsToDollarsNumber } from "utils/convertCentsToDollars";
import { useLocalizedDate } from "utils/date/useLocalizedDate";
import { translateCurrency } from "utils/translations/translateCurrency";

interface Props {
  invoices: Invoice[];
  isLoading: boolean;
  paginationModel: { page: number; pageSize: number };
  onPaginationModelChange: (model: { page: number; pageSize: number }) => void;
  onPreview: (invoice: Invoice) => void;
}

export const InvoiceSpecificationListTable: React.FC<Props> = ({
  invoices,
  isLoading,
  paginationModel,
  onPaginationModelChange,
  onPreview,
}) => {
  const { t } = useTranslation();
  const { parseISO, format } = useLocalizedDate();

  const columns: GridColDef<Invoice>[] = [
    {
      field: "billing_period",
      headerName: t("admin.invoice_specification.invoice"),
      flex: 1.5,
      valueFormatter: (value: string) => (value ? format(parseISO(value), "MMMM yyyy") : "-"),
    },
    {
      field: "status",
      headerName: t("admin.invoice_specification.status"),
      flex: 1,
      renderCell: ({ row }) => (
        <StatusChip className={row.status}>
          <Typography variant="label_bold">
            {row.status === "paid"
              ? t("admin.invoice_specification.status_paid")
              : t("admin.invoice_specification.status_unpaid")}
          </Typography>
        </StatusChip>
      ),
    },
    {
      field: "amount_due",
      headerName: t("admin.invoice_specification.fee"),
      flex: 1,
      valueFormatter: (value: number, row) => translateCurrency(convertCentsToDollarsNumber(value), row.currency),
    },
    {
      field: "preview",
      type: "actions",
      headerName: t("library.exercises.form.preview_header"),
      width: 75,
      getActions: params => [
        <IconButton size="small" onClick={() => onPreview(params.row)}>
          <FontAwesomeIcon icon={faMagnifyingGlass} />
        </IconButton>,
      ],
    },
    {
      field: "stripe",
      type: "actions",
      headerName: t("admin.invoice_specification.view_stripe"),
      width: 120,
      getActions: params => [
        <BaseButton
          variant="action"
          text={t("admin.invoice_specification.view_stripe")}
          onClick={() => params.row.hosted_invoice_url && window.open(params.row.hosted_invoice_url, "_blank")}
          disabled={!params.row.hosted_invoice_url}
        />,
      ],
    },
  ];

  return (
    <MUIDataGrid
      columns={columns}
      rows={invoices}
      getRowId={row => row.invoice_id}
      loading={isLoading}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      rowSelection={false}
      slots={{
        noRowsOverlay: () => <EmptyResultOverlay />,
        noResultsOverlay: () => <EmptyResultOverlay />,
      }}
      localeText={{
        noRowsLabel: "",
        noResultsOverlayLabel: "",
      }}
      disableColumnMenu
      hideHeader
      transparentBackground
      density="comfortable"
      sx={{
        "& .MuiDataGrid-row": {
          cursor: "default",
        },
      }}
    />
  );
};

const StatusChip = styled.div`
  display: flex;
  padding: 4px 8px;
  border-radius: 8px;

  &.paid {
    background-color: ${({ theme }) => theme.colors.redesign.g10};
    border: 1px solid ${({ theme }) => theme.colors.redesign.g100};
    span {
      color: ${({ theme }) => theme.colors.redesign.g100};
    }
  }
  &.open {
    background-color: ${({ theme }) => theme.colors.redesign.o10};
    border: 1px solid ${({ theme }) => theme.colors.redesign.o100};
    span {
      color: ${({ theme }) => theme.colors.redesign.o100};
    }
  }
`;
